import { useState } from 'react';
import Modal from '@carbon/react/es/components/Modal/Modal';
import Button from '@carbon/react/es/components/Button';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';
import Add from '@carbon/icons-react/es/Add';
import Checkmark from '@carbon/icons-react/es/Checkmark';

import { apiFetch } from '../../api/client.js';
import { useT } from '../../i18n/index.js';
import { useProjects } from './use-projects.js';

/**
 * Cible d'un ajout au projet : un type d'objet, un ou plusieurs identifiants (un lot d'images
 * sélectionnées, ou un objet unique), et un libellé d'affichage. L'endpoint et la clé du corps se
 * déduisent du `kind` (`videos`/`videoId`, `images`/`imageId`, `models`/`modelId`).
 */
export type ProjectTarget = {
  kind: 'video' | 'image' | 'model';
  ids: string[];
  label: string;
};

/**
 * Ajoute un objet (ou un lot) à un ou plusieurs projets **transverses** (ADR-0024, incrément 2c).
 * L'API est idempotente : réajouter un objet déjà membre est sans effet, donc on n'a pas besoin de
 * connaître l'appartenance courante — on marque « Ajouté » après un ajout réussi. Le retrait se fait
 * depuis le détail du projet, pas ici.
 */
export function AddToProjectModal({
  target,
  onClose,
}: {
  target: ProjectTarget;
  onClose: () => void;
}) {
  const t = useT();
  const { projects } = useProjects();
  const [added, setAdded] = useState<Set<string>>(new Set());
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const add = async (projectId: string) => {
    setBusyId(projectId);
    setError(null);
    try {
      // Séquentiel : un lot d'images reste modeste, et enchaîner garde l'erreur rattachée à l'objet
      // fautif. L'idempotence de l'API rend un réessai sans risque de doublon.
      for (const id of target.ids) {
        await apiFetch(`/projects/${projectId}/${target.kind}s`, {
          method: 'POST',
          body: { [`${target.kind}Id`]: id },
        });
      }
      setAdded((prev) => new Set(prev).add(projectId));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('data.error.title'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Modal
      open
      passiveModal
      size="sm"
      modalHeading={t('data.addToProject.heading')}
      onRequestClose={onClose}
    >
      <div className="ddp-add-project">
        <p className="ddp-add-project__video">« {truncate(target.label, 60)} »</p>
        {projects.length === 0 ? (
          <p className="ddp-add-project__empty">{t('data.addToProject.empty')}</p>
        ) : (
          <ul className="ddp-add-project__list">
            {projects.map((project) => {
              const isAdded = added.has(project.id);
              return (
                <li key={project.id} className="ddp-add-project__row">
                  <span className="ddp-add-project__name" title={project.description ?? undefined}>
                    {project.name}
                  </span>
                  <Button
                    size="sm"
                    kind={isAdded ? 'ghost' : 'tertiary'}
                    disabled={isAdded || busyId === project.id || target.ids.length === 0}
                    renderIcon={isAdded ? Checkmark : Add}
                    onClick={() => void add(project.id)}
                  >
                    {isAdded ? t('data.addToProject.added') : t('data.addToProject.add')}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
        {error && (
          <InlineNotification
            kind="error"
            title={t('data.error.title')}
            subtitle={error}
            lowContrast
            hideCloseButton
          />
        )}
      </div>
    </Modal>
  );
}

/** Tronque un titre trop long (le titre complet reste en infobulle). */
function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}
